// resources/js/src/services/comment.api.ts
import { request, requestFull } from "./api";
import { ReactionType } from "../components/community/components/post/interfaces/types";

// ============ Raw API Types (زي ما الـ API بيرجّع) ============

export type RawApiComment = {
  id: number;
  post_id: number;
  user_id: number;
  parentId: number | null;
  body: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  reactions_count: number;
  replies: RawApiComment[];
  user: {
    id: number;
    name: string;
    avatarUrl: string | null;
  };
};

export type RawCommentsResponse = {
  success: boolean;
  message?: string;
  data: RawApiComment[];
  meta: {
    per_page: number;
    next_cursor: string | null;
    prev_cursor: string | null;
    has_more: boolean;
  };
};

// ============ UI Types (زي ما الفرونت بيستخدم) ============

export type ApiComment = {
  id: number;
  body: string;
  parentId: number | null;
  author: { id: number; name: string; avatarUrl: string | null };
  isOwner: boolean;
  reactionsCount: number;
  reactions: Array<{
    id: number;
    type: ReactionType;
    user: { id: number; name: string; avatarUrl: string | null };
  }>;
  myReaction: ReactionType;
  edited: boolean;
  createdAt: string;
  updatedAt: string;
  replies: ApiComment[];
};

// ============ Adapter ============

/**
 * يحوّل الـ raw API comment لشكل الـ UI.
 * @param raw الـ comment من الـ API
 * @param currentUserId الـ user الحالي (عشان نحسب isOwner)
 */
export function adaptComment(
  raw: any,
  currentUserId?: number | string | null
): ApiComment {
  const userId = raw.user_id ?? raw.userId ?? raw.user?.id ?? raw.author?.id;
  const userName = raw.user?.name ?? raw.author?.name ?? "User";
  const userAvatar = raw.user?.avatar_url ?? raw.author?.avatarUrl ?? null;

  // ✅ reactions list
  const reactions = (raw.reactions ?? []).map((r: any) => ({
    id: r.id,
    type: r.type,
    user: {
      id: r.user?.id,
      name: r.user?.name ?? "User",
      avatarUrl: r.user?.avatar_url ?? r.user?.avatarUrl ?? null,
    },
  }));

  // ✅ myReaction: أولوية للي جاي من الـ API، fallback من الـ reactions
  const myReaction =
    raw.myReaction ??
    reactions.find(
      (r: any) => currentUserId != null && String(r.user.id) === String(currentUserId)
    )?.type ??
    null;

  const edited = raw.edited ?? (
    raw.updated_at && raw.created_at
      ? new Date(raw.updated_at).getTime() > new Date(raw.created_at).getTime() + 1000
      : raw.updatedAt && raw.createdAt
        ? new Date(raw.updatedAt).getTime() > new Date(raw.createdAt).getTime() + 1000
        : false
  );

  return {
    id: raw.id,
    body: raw.body,
    parentId: raw.parent_id ?? raw.parentId ?? null,
    author: { id: userId, name: userName, avatarUrl: userAvatar },
    isOwner: currentUserId != null && String(currentUserId) === String(userId),
    reactionsCount: raw.reactions_count ?? raw.reactionsCount ?? 0,
    reactions,          // ✅ جديد
    myReaction,         // ✅ محدّث
    edited,
    createdAt: raw.created_at ?? raw.createdAt,
    updatedAt: raw.updated_at ?? raw.updatedAt,
    replies: (raw.replies ?? []).map((r: any) => adaptComment(r, currentUserId)),
  };
}

// ============ API ============

export type CreateCommentPayload = {
  body: string;
  parentId?: number | null;
};

export const commentApi = {
  loadComments: async (
    postId: number | string,
    cursor?: string | null,
    perPage = 15,
    currentUserId?: number | string | null
  ): Promise<{
    data: ApiComment[];
    meta: RawCommentsResponse["meta"];
  }> => {
    const params = new URLSearchParams();
    params.set("per_page", String(perPage));
    if (cursor) params.set("cursor", cursor);

    const res = await requestFull<RawApiComment[]>(
      "GET",
      `/community/posts/${postId}/comments?${params.toString()}`
    );

    return {
      data: (res.data ?? []).map((c) => adaptComment(c, currentUserId)),
      meta: res.meta ?? {
        per_page: perPage,
        next_cursor: null,
        prev_cursor: null,
        has_more: false,
      },
    };
  },

  createComment: async (
    postId: number | string,
    payload: CreateCommentPayload,
    currentUserId?: number | string | null
  ): Promise<ApiComment> => {
    const raw = await request<RawApiComment>(
      "POST",
      `/community/posts/${postId}/comments`,
      payload
    );
    return adaptComment(raw, currentUserId);
  },

  updateComment: async (
    commentId: number | string,
    body: string,
    currentUserId?: number | string | null
  ): Promise<ApiComment> => {
    const raw = await request<RawApiComment>(
      "PATCH",
      `/community/comments/${commentId}`,
      { body }
    );
    return adaptComment(raw, currentUserId);
  },

  deleteComment: (commentId: number | string) =>
    request<null>("DELETE", `/community/comments/${commentId}`),

  reactToComment: async (
    commentId: number | string,
    reaction: ReactionType,
    currentUserId?: number | string | null
  ): Promise<ApiComment> => {
    const raw = await request<RawApiComment>(
      "POST",
      `/community/comments/${commentId}/reactions`,
      { reaction }
    );
    return adaptComment(raw, currentUserId);
  },
};